import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ComposerModePills from './ComposerModePills';
import { useTranslation } from '@/hooks/useTranslation';
import useRequireAuth from '@/hooks/useRequireAuth';
import { useAuthStore, useCoworkerStore, useModalStore } from '@/store';
import { blockIfWorkModeDisabled } from '@/lib/workModeAvailability';

const MODES = [
  {
    id: 'chat',
    labelKey: 'chatInput.coworkerMode.chat',
    hintKey: 'chatInput.coworkerMode.chatHint',
    detailKey: 'chatInput.coworkerMode.chatTooltip',
    noteKey: 'chatInput.coworkerMode.chatCreditNote',
    icon: 'comment',
  },
  {
    id: 'work',
    labelKey: 'chatInput.coworkerMode.coworker',
    hintKey: 'chatInput.coworkerMode.workHint',
    detailKey: 'chatInput.coworkerMode.workTooltip',
    noteKey: 'chatInput.coworkerMode.workCreditNote',
    icon: 'briefcase',
  },
];

export function useComposerModeChange(source = 'composer_coworker_mode') {
  const { t } = useTranslation();
  const requireAuth = useRequireAuth();
  const setComposerMode = useCoworkerStore((s) => s.setComposerMode);
  const isPlus = useAuthStore((s) => s.user?.flags?.isPlus === true);
  const { openModal } = useModalStore((s) => s.actions);
  return useCallback((mode) => {
    if (mode === 'work') {
      if (blockIfWorkModeDisabled()) return;
      const gated = requireAuth(() => true, {
        source,
        title: t('chatInput.coworkerMode.authTitle'),
        subtitle: t('chatInput.coworkerMode.authSubtitle'),
        icon: 'robot',
      });
      if (gated !== true) return;
      if (!isPlus) {
        openModal('PlusFeatureModal', { feature: 'work' });
        return;
      }
      setComposerMode('coworker');
      return;
    }
    setComposerMode('chat');
  }, [isPlus, openModal, requireAuth, setComposerMode, source, t]);
}

/**
 * Chat / Work pill. Lives on new-chat composers (home, collections),
 * not inside an open thread. The parent switches composerMode so send
 * launches a Work thread.
 */
function ComposerModeSwitch({
  value = 'chat',
  onChange,
  compact = false,
  pillsOnly = false,
  hidePills = false,
  placement,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [helpOpen, setHelpOpen] = useState(false);
  const helpId = useId();
  const rootRef = useRef(null);
  const showPills = !hidePills;
  const showCaption = !pillsOnly;

  const handlePress = useCallback((mode) => {
    if (mode === value) return;
    onChange?.(mode);
  }, [onChange, value]);

  const toggleHelp = useCallback(() => {
    setHelpOpen((open) => !open);
  }, []);

  useEffect(() => {
    if (!helpOpen || Platform.OS !== 'web') return undefined;

    const onPointerDown = (event) => {
      const node = rootRef.current;
      if (typeof node?.contains === 'function' && !node.contains(event.target)) {
        setHelpOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setHelpOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [helpOpen]);

  const caption = showCaption ? (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 3,
        marginTop: showPills ? (compact ? 8 : 10) : 0,
      }}
    >
      <Text
        size="s"
        color="secondary"
        weight="medium"
      >
        {t('chatInput.coworkerMode.chooseTitle')}
      </Text>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('chatInput.coworkerMode.helpA11y')}
        accessibilityState={{ expanded: helpOpen }}
        {...(Platform.OS === 'web' ? {
          'aria-expanded': helpOpen,
          'aria-controls': helpOpen ? helpId : undefined,
        } : {})}
        hitSlop={8}
        onPress={toggleHelp}
        style={({ hovered, pressed }) => ({
          width: 24,
          height: 24,
          borderRadius: 12,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: helpOpen || hovered || pressed
            ? colors.solid_color_tertiary
            : 'transparent',
          ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
        })}
      >
        <Icon
          name="circle-question"
          size={15}
          color={helpOpen ? colors.text_color_primary : colors.text_color_secondary}
        />
      </Pressable>
    </View>
  ) : null;

  return (
    <View ref={rootRef} style={{ alignItems: 'center', maxWidth: '100%' }}>
      {showPills && <ComposerModePills value={value} onChange={handlePress} colors={colors} compact={compact} placement={placement}
        label={t('chatInput.coworkerMode.chooseTitle')} modes={MODES.map(item => ({ id: item.id, label: t(item.labelKey), hint: t(item.hintKey) }))} />}
      {caption}
      {showCaption && helpOpen ? (
        <View
          nativeID={helpId}
          style={{
            marginTop: compact ? 8 : 12,
            width: compact ? 320 : 360,
            maxWidth: '100%',
            padding: compact ? 16 : 20,
            borderRadius: 18,
            backgroundColor: colors.solid_color_primary,
            borderWidth: 1,
            borderColor: colors.border_color_secondary,
            ...(Platform.OS === 'web' ? {
              boxShadow: '0 8px 28px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.03)',
            } : {
              shadowColor: '#000000',
              shadowOffset: { width: 0, height: 6 },
              shadowOpacity: 0.08,
              shadowRadius: 16,
              elevation: 3,
            }),
          }}
        >
          {MODES.map((item, index) => (
            <View
              key={item.id}
              style={{
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 12,
                ...(index > 0 ? {
                  marginTop: 16,
                  paddingTop: 16,
                  borderTopWidth: 1,
                  borderTopColor: colors.border_color_secondary,
                } : {}),
              }}
            >
              <View
                accessibilityElementsHidden
                importantForAccessibility="no-hide-descendants"
                {...(Platform.OS === 'web' ? { 'aria-hidden': true } : {})}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  backgroundColor: colors.solid_color_tertiary,
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon name={item.icon} size={16} color={colors.text_color_primary} />
              </View>
              <View style={{ flex: 1, minWidth: 0, gap: 5 }}>
                <Text size="n" weight="bold" style={{ lineHeight: 20 }}>
                  {t(item.labelKey)}
                </Text>
                <Text size="s" color="secondary" style={{ lineHeight: 19 }}>
                  {t(item.detailKey)}
                </Text>
                <Text size="xs" color="secondary" weight="medium" style={{ lineHeight: 17, marginTop: 2 }}>
                  {t(item.noteKey)}
                </Text>
              </View>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

export default React.memo(ComposerModeSwitch);
